import { uuidv7 } from '@didto/domain';
import type { CommandKind, CommandPayloads } from '@didto/domain';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { runCommand } from '@/lib/api';
import type { CommandResult } from '@/lib/api';
import { PAUSED_REASON, currentConnectivity } from '@/lib/connectivity';
import { isNetworkError, toAppError } from '@/lib/errors';
import type { AppError } from '@/lib/errors';
import { useToast } from './toast';

/**
 * Operational commands that go straight to the server (KDS bumps, serves): no outbox, no hidden
 * replay (Phase 5 refinement 1). A command that cannot be confirmed stays on its card as
 * "Action not confirmed" until the employee retries it with the same command id (the server
 * replays instead of applying twice) or discards it.
 */
export interface PendingAction {
  commandId: string;
  kind: CommandKind;
  payload: unknown;
  /** Ticket, item or order id the action belongs to (one in flight per target). */
  target: string;
  label: string;
  state: 'sending' | 'unconfirmed';
}

export type ActionOutcome =
  { ok: true; result: CommandResult } | { ok: false; unconfirmed: boolean; error: AppError };

export function useDirectActions(
  locationId: string | null,
  invalidate: readonly (readonly string[])[],
) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pending, setPending] = useState<Record<string, PendingAction>>({});
  const keyStr = JSON.stringify(invalidate);

  const drop = (target: string) =>
    setPending((cur) => {
      const { [target]: _gone, ...rest } = cur;
      return rest;
    });

  const send = useCallback(
    async (p: PendingAction): Promise<ActionOutcome> => {
      setPending((cur) => ({ ...cur, [p.target]: { ...p, state: 'sending' } }));
      const keys = JSON.parse(keyStr) as string[][];
      try {
        const result = await runCommand(p.kind, p.payload as never, {
          commandId: p.commandId,
          ...(locationId ? { locationId } : {}),
        });
        drop(p.target);
        await Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })));
        return { ok: true, result };
      } catch (e) {
        const error = toAppError(e);
        if (isNetworkError(e)) {
          setPending((cur) => ({ ...cur, [p.target]: { ...p, state: 'unconfirmed' } }));
          return { ok: false, unconfirmed: true, error };
        }
        drop(p.target);
        for (const k of keys) void qc.invalidateQueries({ queryKey: k });
        return { ok: false, unconfirmed: false, error };
      }
    },
    [locationId, qc, keyStr],
  );

  const run = useCallback(
    <K extends CommandKind>(
      kind: K,
      payload: CommandPayloads[K],
      target: string,
      label: string,
      opts: { quiet?: boolean } = {},
    ): Promise<ActionOutcome> => {
      // ADR-037: a state change needs the server now; nothing is started without it
      if (currentConnectivity() !== 'online') {
        if (!opts.quiet) toast(PAUSED_REASON, 'danger');
        return Promise.resolve({
          ok: false,
          unconfirmed: false,
          error: { code: 'network', message: PAUSED_REASON },
        });
      }
      return send({ commandId: uuidv7(), kind, payload, target, label, state: 'sending' }).then(
        (r) => {
          if (!r.ok && !r.unconfirmed && !opts.quiet) toast(r.error.message, 'danger');
          return r;
        },
      );
    },
    [send, toast],
  );

  const retry = useCallback(
    (target: string) => {
      const p = pending[target];
      if (!p || p.state !== 'unconfirmed') return;
      if (currentConnectivity() !== 'online') {
        toast(PAUSED_REASON, 'danger');
        return;
      }
      void send(p).then((r) => {
        if (!r.ok && !r.unconfirmed) toast(r.error.message, 'danger');
      });
    },
    [pending, send, toast],
  );

  const discard = useCallback((target: string) => drop(target), []);

  return { pending, run, retry, discard };
}
